import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { BrandSummary, Tag } from "@shared/types";
import { buildTagIntro } from "@shared/seoContent";
import { api } from "../lib/api";
import { BrandGrid } from "../components/BrandGrid";
import { EmptyState } from "../components/EmptyState";
import { isAbortError } from "../lib/asyncState";

export function TagPage() {
  const { slug } = useParams<{ slug: string }>();
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.tags.list().then((r) => setTags(r.tags)).catch(() => setTags([]));
  }, []);

  useEffect(() => {
    if (!slug) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api.tags.brands(slug, { sort: "crowd", limit: 48 }, { signal: controller.signal })
      .then((r) => {
        setBrands(r.items);
        setTotal(r.total);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setBrands([]);
        setTotal(0);
        setError("Could not load this signal right now.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, reloadKey]);

  const current = tags.find((tag) => tag.slug === slug);
  const title = current?.name ?? formatSlug(slug ?? "");

  return (
    <div className="container" style={{ paddingBlock: "2rem" }}>
      <Link to="/brands" className="meta" style={{ display: "inline-block", marginBottom: "0.75rem" }}>
        All brands
      </Link>
      <div className="section__head">
        <h2>{title}</h2>
        <div style={{ color: "var(--fg-subtle)", fontSize: "0.9rem" }}>
          {total === 1 ? "1 brand" : `${total} brands`}
        </div>
      </div>
      {current && brands.length > 0 && (
        <p className="taxonomy-intro">{buildTagIntro(current, brands, current.type === "style_direction" ? "style" : "tag")}</p>
      )}
      {loading ? (
        <div className="empty-state">Loading...</div>
      ) : error ? (
        <EmptyState title="Signal could not load" actions={<button className="primary" onClick={() => setReloadKey((key) => key + 1)}>Try again</button>}>
          {error}
        </EmptyState>
      ) : brands.length ? (
        <BrandGrid brands={brands} />
      ) : (
        <EmptyState
          title="No brands for this vibe yet"
          actions={
            <>
            <Link to="/brands"><button className="primary">Browse brands</button></Link>
            <Link to="/categories"><button>View categories</button></Link>
            </>
          }
        >
          This signal is not active on the public radar right now.
        </EmptyState>
      )}
    </div>
  );
}

function formatSlug(value: string): string {
  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
